import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import { outerLinks } from '~/@common/constants/strings'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { errorHandlerService, toastService, webviewService } from '~/@common/services'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors } from '~/@common/styles'
import _Switch from '~/@common/ui/_Switch/_Switch'
import { Icon } from '~/@common/ui/Icon/Icon'
import type { IconNames } from '~/@common/ui/Icon/iconNames.type'
import Modal from '~/@common/ui/modal/Modal'
import { openLink } from '~/@common/utils/openLink'

import OpinionModal from '../@widgets/(Navigation)/GNB/OpinionModal'
import PasswordChangeModal from '../@widgets/PasswordChangeModal'
import { homeApi } from '../home/@common/api'
import { S } from '.'
import ParentProfile from './@components/ParentProfile/ParentProfile'

const ProfilePage = observer(() => {
  const { student, studentSchoolTypeWithGrade } = commonRepo
  const isParent = commonRepo.permission.isParent
  const isAllowedToUpdateStudentInfo =
    commonRepo.permission.byFeature.isAllowed('STUDENT_INFO_UPDATE')
  const [pushEnabled, setPushEnabled] = useState(false)

  const { isMobile } = useStudentAppMediaQuery()
  const navigate = useNavigate()

  // 푸시 노티 차단 — PUSH restriction 으로 식별 (B2G/스쿨플랫엔 BE 가 mathflat.student_parent.push 내려줌)
  const isPushNotificationAvailable =
    webviewService.isApp &&
    commonRepo.permission.byFeature.isAllowed('PUSH') &&
    webviewService.isPushNotificationAvailableVersion

  useEffect(() => {
    // PUSH 차단 계정은 학생용 푸시 권한을 사용하지 않음 — 불필요한 API 호출/403 방지
    if (!isPushNotificationAvailable) return

    const fetchPushEnabled = async () => {
      const pushEnabled = await homeApi.getNotificationPermission()
      setPushEnabled(pushEnabled)
    }

    fetchPushEnabled()
  }, [isPushNotificationAvailable])

  // PARENT → 학부모 전용 프로필
  if (isParent) {
    return <ParentProfile />
  }

  const handlePushEnabledChange = async (pushEnabled: boolean) => {
    try {
      await homeApi.patchPushNotificationPermission(pushEnabled)
      setPushEnabled(pushEnabled)
      if (pushEnabled) {
        webviewService.revalidatePermission()
        toastService.success('푸시 알림을 받을 수 있어요.')
      }
    } catch (error) {
      errorHandlerService.handle(error)
    }
  }

  return (
    <>
      <S.Wrapper>
        <S.Profile>
          <div className="profile-box">
            <div className="profile-content">
              <header data-no-divider={!isAllowedToUpdateStudentInfo}>
                <h3 className="name">{student?.name}</h3>
                {!isMobile && isPushNotificationAvailable && (
                  <div className="switch-wrapper">
                    <p>학습 관련 알림</p>
                    <_Switch
                      checked={pushEnabled}
                      onChange={(e) => handlePushEnabledChange(e.target.checked)}
                    />
                  </div>
                )}
              </header>
              <div className="info">
                <div className="info-label">
                  <h4>학년</h4>
                  {isAllowedToUpdateStudentInfo && (
                    <>
                      <h4>아이디</h4>
                      <h4>출석 번호</h4>
                    </>
                  )}
                </div>
                <div className="info-value">
                  <p>{studentSchoolTypeWithGrade}</p>
                  {isAllowedToUpdateStudentInfo && (
                    <>
                      <p>{student?.loginId}</p>
                      <p>{student?.attendanceCode}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="button-container">
              {isAllowedToUpdateStudentInfo && (
                <button
                  className="password-change"
                  onClick={() => {
                    modalService.openModal(
                      {
                        content: <PasswordChangeModal />,
                        header: '비밀번호 변경',
                      },
                      {
                        modalName: '비밀번호 변경',
                        hasCloseButton: isMobile,
                        headerOption: { hasBottomBorder: false },
                      },
                    )
                  }}
                >
                  <Icon name="icon_edit" size={18} color={colors.gray.$700} />
                  <p>비밀번호 변경</p>
                </button>
              )}
              <button
                className="password-change"
                onClick={() => {
                  modalService.openModal(
                    <Modal.Confirm.Negative
                      cancel={{ children: '취소' }}
                      confirm={{
                        onClick: () => {
                          authService.logout()
                          modalService.closeModal()
                        },
                        children: '로그아웃',
                      }}
                    >
                      로그아웃 하시겠습니까?
                    </Modal.Confirm.Negative>,
                    { modalName: '로그아웃 모달' },
                  )
                }}
              >
                <Icon name="icon_unlock" size={18} color={colors.gray.$700} />
                <p>로그아웃</p>
              </button>
            </div>
          </div>
        </S.Profile>
        {isMobile && (
          <S.Etc>
            {isPushNotificationAvailable && (
              <div className="notification-wrapper">
                <div className="notification-content">
                  <div className="notification-title">푸시 알림</div>
                  <div className="notification-description">
                    오늘의 학습, 학습지 {tJosa('출제')} 정보 등
                  </div>
                </div>
                <_Switch
                  checked={pushEnabled}
                  onChange={(e) => handlePushEnabledChange(e.target.checked)}
                />
              </div>
            )}
            <ListButton
              iconName="icon_heart_comment"
              text="의견 남기기"
              onClick={() =>
                modalService.openModal(<OpinionModal />, {
                  hasCloseButton: true,
                  modalName: 'opinion-modal',
                  modalStyle: {
                    padding: '0px',
                  },
                })
              }
            />
            {/* Tip: 가이드 동영상 준비 되어있지 않아, 스쿨플랫에서는 프리미엄 학생앱 사용 가이드 노션 페이지로 대체 */}
            {commonRepo.permission.byFeature.isAllowed('USER_GUIDE') && (
              <ListButton
                iconName="icon_help"
                text="사용가이드 보기"
                onClick={() => {
                  if (commonRepo.isSchoolflat) {
                    openLink(outerLinks.스쿨플랫_학생앱_사용_가이드_링크)
                  } else {
                    navigate(routeName.guideVideo)
                  }
                }}
              />
            )}
          </S.Etc>
        )}
      </S.Wrapper>
    </>
  )
})

const ListButton = ({
  iconName,
  text,
  onClick,
}: {
  iconName: IconNames
  text: string
  onClick?: () => void
}) => {
  return (
    <button className="etc-card" onClick={onClick}>
      <Icon name={iconName} size={18} color={colors.gray.$700} />
      <p>{text}</p>
      <Icon name="icon_chevron_right" size={18} color={colors.gray.$700} />
    </button>
  )
}

export default ProfilePage
